import { useMemo } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import useDebounce from '../queries/auth/useDebounce';
import { getPasswordStrength, PASSWORD_STRENGTH_LABELS } from '../utils/zxcvbn.util';

// zxcvbn's matching cost grows with input length/repetition, and re-running it on every
// keystroke was making long passwords feel sluggish to type. Only recompute once typing
// pauses - the meter just lags the last keystroke by this long, which is fine for advisory UI.
const SCORE_DEBOUNCE_MS = 400;

const getStrengthColor = (theme, score) =>
  [
    theme.palette.error.main,
    theme.palette.error.light,
    theme.palette.warning.main,
    theme.palette.success.light,
    theme.palette.success.main,
  ][score];

// Advisory only - doesn't block anything, just gives the user live feedback on how guessable
// their password is. Renders nothing until there's a password to score.
const PasswordStrengthMeter = ({ password, userInputs = [] }) => {
  const debouncedPassword = useDebounce(password, SCORE_DEBOUNCE_MS);

  const result = useMemo(
    () => getPasswordStrength(debouncedPassword, userInputs),
    [debouncedPassword, userInputs],
  );

  if (!result) {
    return null;
  }

  const { score } = result;

  return (
    <Stack spacing={0.5} sx={{ width: '100%', mt: 0.5 }}>
      <LinearProgress
        variant="determinate"
        value={((score + 1) / PASSWORD_STRENGTH_LABELS.length) * 100}
        sx={theme => ({
          height: 8,
          borderRadius: theme.spacing(1),
          backgroundColor: theme.palette.grey[300],
          [`& .${linearProgressClasses.bar}`]: {
            borderRadius: theme.spacing(1),
            backgroundColor: getStrengthColor(theme, score),
          },
        })}
      />
      <Typography
        sx={theme => ({
          fontSize: theme.typography.pxToRem(12),
          fontWeight: 500,
          color: getStrengthColor(theme, score),
        })}>
        {PASSWORD_STRENGTH_LABELS[score]}
      </Typography>
    </Stack>
  );
};

export default PasswordStrengthMeter;
